import PropTypes from 'prop-types';
import { between, forbidExtraProps } from 'airbnb-prop-types';

import { Link } from 'react-router';

import { connect } from 'react-redux';

import get from 'lodash/fp/get';
import isEmpty from 'lodash/fp/isEmpty';
import toLower from 'lodash/fp/toLower';

import { theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import * as hairProductContent from 'assets/content/haircareProducts';
import * as skinProductContent from 'assets/content/skincareProducts';

import useMedia from 'utils/useMedia';

import * as membershipSelectors from 'dux/membership/selectors';

import Button from './MembershipPercsButton';
import MembershipPercsCard, {
  PERCS_CARD_MIN_HEIGHT_DESKTOP,
  PERCS_CARD_MIN_HEIGHT_MOBILE,
  PERCS_CARD_PADDING_VERTICAL,
} from './MembershipPercsCard';
import PunchCard from './PunchCard';

dayjs.extend(utc);

const HalfCircleProductShowcase = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 250px;
  height: 250px;
  margin-top: -100px;

  border-top-left-radius: 250px;
  border-top-right-radius: 250px;
  background-color: ${theme.colors.tertiary[100]};
`;

const SelectedProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 175px;
  margin: 80px -18px -48px -18px;
  padding: 18px;

  background-color: ${theme.colors.tertiary[100]};
`;

const TopWrapper = styled.div`
  min-height: ${props => (props?.minheight ? `${props.minheight}px` : 'initial')};
`;

const productContent = { ...hairProductContent, ...skinProductContent };

export const ComplementaryGiftPercsCard = ({
  canChooseProduct,
  expirationDate,
  points,
  selectedSlug,
}) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
  const productsToGo = 10 - points;

  return (
    <MembershipPercsCard>
      <TopWrapper
        minheight={
          canChooseProduct
            ? isMobile
              ? PERCS_CARD_MIN_HEIGHT_MOBILE + PERCS_CARD_PADDING_VERTICAL - 175
              : PERCS_CARD_MIN_HEIGHT_DESKTOP + PERCS_CARD_PADDING_VERTICAL - 175
            : 'auto'
        }
      >
        <Typography paragraph variant="h3">
          <span className="hide-text-in-percy">
            {productsToGo} product{productsToGo !== 1 && 's'}
          </span>
          <span> to go ‘til your free one!</span>
        </Typography>

        <Typography paragraph variant="p3">
          Order 10 products, and the 11th one is on us. Then the cycle starts again, so there’s
          always something to look forward to.
        </Typography>

        <PunchCard points={points} rows={isMobile ? 3 : 2} />
      </TopWrapper>

      {canChooseProduct ? (
        <SelectedProductContainer data-testid={`selected-slug-${selectedSlug ?? 'none'}`}>
          <HalfCircleProductShowcase>
            {selectedSlug ? (
              <img
                alt={get(`${selectedSlug}.label`, productContent)}
                height={58}
                src={get(`${selectedSlug}.picture`, productContent).src}
              />
            ) : (
              <Typography variant="h4">
                Don’t forget your
                <br />
                free one!
              </Typography>
            )}
          </HalfCircleProductShowcase>

          {selectedSlug ? (
            <Typography variant={isMobile ? 'p3' : 'p2'}>
              Your complimentary {toLower(get(`${selectedSlug}.label`, productContent))} will ship
              with your next {get(`${selectedSlug}.category`, productContent)} subscription order
            </Typography>
          ) : (
            <Typography variant={isMobile ? 'p3' : 'p2'}>
              Go ahead and pick any product you want to come free in your next order—it’s on us.
            </Typography>
          )}

          {selectedSlug ? (
            <Button
              Component={Link}
              data-click="complimentary-change-product"
              data-from="membership-lp-subscribers"
              data-testid="change-product"
              to="/the-salon/choose-product"
              variant="lime"
            >
              Change your free product
            </Button>
          ) : (
            <Button
              Component={Link}
              data-click="complimentary-choose-product"
              data-from="membership-lp-subscribers"
              data-testid="choose-free-product-cta"
              to="/the-salon/choose-product"
              variant="lime"
            >
              CHOOSE YOUR FREE PRODUCT
            </Button>
          )}

          {isEmpty(selectedSlug) && !isEmpty(expirationDate) && (
            <Typography color="grey" variant="p3">
              Claim by {dayjs(new Date(expirationDate)).utc().format('MM/DD/YY')}
              <span>. Excludes oils and supplements.</span>
            </Typography>
          )}
        </SelectedProductContainer>
      ) : (
        <Button data-testid="choose-free-product-cta" disabled variant="lime">
          CHOOSE YOUR FREE PRODUCT
        </Button>
      )}
    </MembershipPercsCard>
  );
};

ComplementaryGiftPercsCard.propTypes = forbidExtraProps({
  canChooseProduct: PropTypes.bool,
  expirationDate: PropTypes.string,
  // Real life use : User has a variable number of points punched on his card
  points: between({ gte: 0, lte: 10 }),
  selectedSlug: PropTypes.string,
});

ComplementaryGiftPercsCard.defaultProps = {
  canChooseProduct: false,
  expirationDate: null,
  points: 0,
  selectedSlug: null,
};

export default connect(
  state => ({
    canChooseProduct: membershipSelectors.canSelectFreeProduct(state),
    expirationDate: membershipSelectors.getExpirationDate(state),
    points: membershipSelectors.getNumberOfProductBoughtSinceLastUnlock(state),
    selectedSlug: membershipSelectors.getCurrentSelectedFreeProductSlug(state),
  }),
  {}
)(ComplementaryGiftPercsCard);
